import React, { useEffect, useRef, useState } from "react";
import "./IDSLab.css";

const API_URL =
  "https://op5a5iw1c1.execute-api.ap-south-1.amazonaws.com/dev/sql-injection"; // reuse your gateway

// Robust callApi that handles backend "outer" wrapper with string body
async function callApi(payload) {
  const res = await fetch(API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const text = await res.text();
  let outer;
  try {
    outer = JSON.parse(text);
  } catch (e) {
    // fallback: not JSON
    throw new Error("Backend did not return JSON: " + text);
  }

  // outer is an object like { statusCode, headers, body }
  // body may be JSON string or plain string
  let inner;
  if (typeof outer.body === "string") {
    try {
      inner = JSON.parse(outer.body);
    } catch (_) {
      inner = { error: outer.body };
    }
  } else {
    inner = outer.body || outer;
  }

  if (outer.statusCode && outer.statusCode >= 400) {
    const msg =
      inner && (inner.error || inner.message)
        ? inner.error || inner.message
        : outer.body || "Request failed";
    throw new Error(msg);
  }

  return inner;
}

// Simple RuleRow for IDS signature rules
function RuleRow({ rule, index, onChange, onDelete }) {
  const update = (k, v) => onChange(index, { ...rule, [k]: v });
  return (
    <div className="rule-row">
      <input
        type="checkbox"
        checked={!!rule.enabled}
        onChange={(e) => update("enabled", e.target.checked)}
      />
      <input
        className="rule-id"
        value={rule.id || ""}
        placeholder="id"
        onChange={(e) => update("id", e.target.value)}
      />
      <input
        className="rule-pattern"
        value={rule.pattern || ""}
        placeholder="regex pattern"
        onChange={(e) => update("pattern", e.target.value)}
      />
      <input
        value={rule.service || ""}
        placeholder="service (HTTP/SSH...)"
        onChange={(e) => update("service", e.target.value)}
      />
      <input
        value={rule.ports || ""}
        placeholder="ports e.g. 80,22"
        onChange={(e) => update("ports", e.target.value)}
      />
      <select
        value={rule.action || "alert"}
        onChange={(e) => update("action", e.target.value)}
      >
        <option value="alert">ALERT</option>
      </select>
      <button className="danger" onClick={() => onDelete(index)} title="Delete">
        ✕
      </button>
    </div>
  );
}

function LogViewer({ logs }) {
  return (
    <div className="log-viewer">
      <h4>Multi-logs</h4>
      <div className="log-list">
        {logs.slice(0, 200).map((l, i) => (
          <div key={i} className="log-item">
            <strong>{l[0]}</strong>:{" "}
            {typeof l[1] === "string" ? l[1] : JSON.stringify(l[1])}
          </div>
        ))}
      </div>
    </div>
  );
}

function AlertsPanel({ alerts }) {
  return (
    <div className="alerts-panel">
      <h4>Alerts</h4>
      <div className="alerts-list">
        {alerts.slice(0, 200).map((a, i) => (
          <div key={i} className="alert-item">
            <div className="alert-type">
              {a.type || a.rule_id || "signature"}
            </div>
            <div className="alert-meta">
              {a.src_ip} ➜ {a.dst_ip}:{a.dst_port || "-"}
            </div>
            <pre className="alert-snippet">
              {(a.payload_snippet || a.evidence || "").slice(0, 200)}
            </pre>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function IDSLab() {
  const [terminal, setTerminal] = useState("Ready.\n");
  const [session, setSession] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [rules, setRules] = useState([
    {
      id: "r_sqli",
      enabled: true,
      pattern: "or 1=1|union select|sleep\\(",
      service: "HTTP",
      ports: "80",
      action: "alert",
    },
    {
      id: "r_xss",
      enabled: true,
      pattern: "<script|%3Cscript%3E",
      service: "HTTP",
      ports: "80",
      action: "alert",
    },
  ]);
  const [injectText, setInjectText] = useState("");
  const [intensity, setIntensity] = useState(1.0);
  const [scenario, setScenario] = useState("web_server");
  const [events, setEvents] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [multiLogs, setMultiLogs] = useState([]);
  const tickTimer = useRef(null);

  const appendTerm = (s) => setTerminal((t) => t + s);

  // init
  const init = async () => {
    try {
      const payload = {
        method: "ids_full",
        action: "init",
        scenario,
        intensity,
        mode: "api",
      };
      const res = await callApi(payload);
      appendTerm(res.terminal || "\n");
      setSession(res.session || res);
    } catch (e) {
      appendTerm(`[!] Error: ${e.message}\n`);
    }
  };

  useEffect(() => {
    init();
  }, []);

  const doTick = async () => {
    try {
      const payload = {
        method: "ids_full",
        action: "tick",
        session,
        scenario,
        intensity,
        rules,
        inject_logs: injectText ? [injectText] : [],
        count: 120,
      };
      const res = await callApi(payload);
      appendTerm(res.terminal || "\n");
      setSession(res.session || session);
      // prepend latest events & alerts
      setEvents((e) => [...res.events.slice(0, 200), ...e].slice(0, 500));
      setAlerts((a) => [...res.alerts, ...a].slice(0, 500));
      setMultiLogs((m) => [...res.multi_logs, ...m].slice(0, 500));
    } catch (e) {
      appendTerm(`[!] Error: ${e.message}\n`);
      stop();
    }
  };

  const start = () => {
    if (isRunning) return;
    setIsRunning(true);
    // run immediately then interval
    doTick();
    tickTimer.current = setInterval(() => doTick(), 1400);
  };
  const stop = () => {
    setIsRunning(false);
    clearInterval(tickTimer.current);
  };

  const step = () => doTick();

  const reset = () => {
    stop();
    setTerminal("Ready.\n");
    setEvents([]);
    setAlerts([]);
    setMultiLogs([]);
    setSession(null);
    init();
  };

  // rules editing helpers
  const addRule = () =>
    setRules((r) => [
      ...r,
      { id: `r_${Date.now()}`, enabled: true, pattern: "", action: "alert" },
    ]);
  const changeRule = (idx, r) =>
    setRules((rs) => rs.map((x, i) => (i === idx ? r : x)));
  const deleteRule = (idx) => setRules((rs) => rs.filter((_, i) => i !== idx));

  return (
    <div className="ids-container">
      <h1>🔎 IDS Simulation Lab</h1>
      <div className="top-controls">
        <div className="left">
          <label>Scenario</label>
          <select
            value={scenario}
            onChange={(e) => setScenario(e.target.value)}
            disabled={isRunning}
          >
            <option value="web_server">Web Server</option>
            <option value="workstation">Workstation</option>
            <option value="mail_server">Mail Server</option>
            <option value="dmz_app">DMZ App</option>
            <option value="iot_device">IoT Device</option>
          </select>

          <label>Intensity: {intensity}</label>
          <input
            type="range"
            min={0.2}
            max={3.0}
            step={0.1}
            value={intensity}
            onChange={(e) => setIntensity(parseFloat(e.target.value))}
          />

          <div className="buttons">
            {isRunning ? (
              <button onClick={stop}>Stop</button>
            ) : (
              <button onClick={start}>Start</button>
            )}
            <button onClick={step} disabled={isRunning}>
              Step
            </button>
            <button className="ghost" onClick={reset}>
              Reset
            </button>
          </div>
        </div>

        <div className="right">
          <label>Inject log (one-per-tick)</label>
          <textarea
            style={{
              width: "100%",
              lineHeight: "1.2em", // height of each line
              height: "9.2em", // 6.5 × 1.2em ≈ 7.8em
            }}
            value={injectText}
            onChange={(e) => setInjectText(e.target.value)}
            placeholder="Paste a custom log line or JSON event"
          />
          <div className="session-box">
            Session:{" "}
            {session ? `tick=${session.tick} seed=${session.seed}` : "null"}
          </div>
        </div>
      </div>

      <div className="main-grid">
        <div className="rules-panel">
          <h3>Rules</h3>
          <div className="rules-list">
            {rules.map((r, idx) => (
              <RuleRow
                key={idx}
                rule={r}
                index={idx}
                onChange={changeRule}
                onDelete={deleteRule}
              />
            ))}
            <div className="rules-footer">
              <button onClick={addRule}>+ Add Rule</button>
            </div>
          </div>
        </div>

        <div className="alerts-logs">
          <AlertsPanel alerts={alerts} />
          <LogViewer logs={multiLogs} />
        </div>

        <div className="terminal-panel">
          <h3>Terminal</h3>
          <pre className="terminal">{terminal}</pre>
        </div>
      </div>
    </div>
  );
}
